'use client';

import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { type ReactElement, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { Button } from '@/app/components/client/button';
import { CmsImage } from '@/app/components/server/cms-image';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type BasePageParams } from '@/app/urls/url-mapping';
import {
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { type GaPageData } from '@/types';
import { HeroApplyForm } from '../hero-apply-form';

export type HeroWithApplyForm = {
  button: DatoButtonBlock | null;
  form: [HeroApplyForm];
  headingProps?: TextProps;
  image: DatoImage;
  moduleFormat: [DatoModuleFormat];
  subtitle: string;
  title: string;
};

type Props = {
  data: HeroWithApplyForm;
  params: BasePageParams;
  gaPageData: GaPageData;
} & BoxProps;

export const HeroWithApplyForm = (props: Props): ReactElement => {
  const {
    as = 'header',
    data: {
      button,
      form: [form],
      headingProps = {},
      image,
      moduleFormat: [moduleFormat],
      subtitle,
      title,
    },
    gaPageData,
    params,
    ...boxProps
  } = props;
  const [firstRenderDone, setFirstRenderDone] = useState(false);
  useEffectOnce(() => {
    setFirstRenderDone(true);
  });

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column' }}
      moduleFormat={moduleFormat}
      outerProps={{ as, ...boxProps }}
    >
      <Flex direction={['column', null, 'row']} gap={[4, null, 3]}>
        <Flex direction="column" gap={2} justify="center">
          <Text
            as="h1"
            textStyle={['5xl', null, '6xl', '7xl']}
            {...headingProps}
          >
            {title}
          </Text>
          <Text textStyle="lBold">{subtitle}</Text>
          {button && <Button {...button} />}
        </Flex>

        {!firstRenderDone ? (
          <Box h="400px" w="full" />
        ) : (
          <>
            {isSmallScreen && (
              <Flex as="figure" flexShrink={0} justifyContent="center" w="full">
                <CmsImage
                  data={{ ...image.responsiveImage }}
                  sizes="328px"
                  style={{
                    height: 328,
                    width: 328,
                  }}
                  {...(as === 'header' && { priority: true })}
                />
              </Flex>
            )}
            <HeroApplyForm
              applyForm={form}
              flexShrink={0}
              gaPageData={gaPageData}
              mt={[-4, null, 0]}
              mx={[-2, null, 0]}
              params={params}
            />
          </>
        )}
      </Flex>
    </ModuleWrapper>
  );
};
